import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';

interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
}

interface AddModuleModalProps {
  visible: boolean;
  courseId: string;
  editingModule?: Module | null;
  onHide: () => void;
  onSuccess?: () => void;
}

const AddModuleModal: React.FC<AddModuleModalProps> = ({
  visible,
  courseId,
  editingModule,
  onHide,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 1,
    status: 'active'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Draft', value: 'draft' }
  ];

  useEffect(() => {
    if (editingModule) {
      setFormData({
        title: editingModule.title,
        description: editingModule.description,
        order: editingModule.order,
        status: editingModule.status || 'active'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        order: 1,
        status: 'active'
      });
    }
    setErrors({});
    setError(null);
  }, [editingModule, visible]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const showError = (detail: string, summary = 'Error') => {
    toast.current?.show({
      severity: 'error',
      summary,
      detail,
      life: 4500,
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.order || formData.order < 1) {
      newErrors.order = 'Order must be at least 1';
    }

    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;

    if (!isValid) {
      showError('Please correct all highlighted fields', 'Validation Failed');
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      course_id: courseId,
      title: formData.title.trim(),
      description: formData.description.trim(),
      order: formData.order,
      status: formData.status
    };

    console.log('Module Payload:', payload);

    try {
      if (editingModule) {
        // Update module
        const response = await apiClient.patch(`/v1/modules/${editingModule.id}`, payload);

        if (response.data.success) {
          onSuccess?.();
          setTimeout(() => onHide(), 900);
        } else {
          showError(response.data.message || 'Operation failed');
        }
      } else {
        // Create module
        const response = await apiClient.post('/v1/modules', payload);

        if (response.data.success) {
          onSuccess?.();
          setTimeout(() => onHide(), 900);
        } else {
          showError(response.data.message || 'Operation failed');
        }
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to save module. Please try again.';
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      order: 1,
      status: 'active'
    });
    setErrors({});
    setError(null);
    onHide();
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '600px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: {
          style: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        },
      }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        <div
          style={{
            background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              background: 'rgba(212, 241, 126, 0.1)',
              borderRadius: '50%',
              filter: 'blur(40px)',
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                background: 'rgba(212, 241, 126, 0.2)',
                borderRadius: '16px',
                marginBottom: '8px',
                fontSize: '0.65rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Module Management
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
              {editingModule ? 'Edit Module' : 'Create Module'}
            </h2>
            <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
              {editingModule ? 'Update module details' : 'Add a new module to organize course content'}
            </p>
          </div>
        </div>

        {error && (
          <Message
            severity="error"
            text={error}
            style={{ marginBottom: '20px', fontSize: '0.9rem' }}
          />
        )}

        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid #f0f0f0',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <GlobalInputField
              label="Module Title"
              required
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., Module 1: Introduction to Programming"
              error={errors.title}
            />

            <GlobalTextArea
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description of what this module covers..."
              error={errors.description}
              rows={4}
            />

            <GlobalInputField
              label="Order"
              required
              type="number"
              value={formData.order.toString()}
              onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
              placeholder="1"
              error={errors.order}
            />

            <GlobalDropdownField
              label="Status"
              required
              value={formData.status}
              onChange={(val) => handleInputChange('status', val)}
              options={statusOptions}
              placeholder="Select status"
              error={errors.status}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlobalButton
            label="Cancel"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          />
          <GlobalButton
            label={
              loading
                ? editingModule
                  ? 'Updating...'
                  : 'Creating...'
                : editingModule
                ? 'Update Module'
                : 'Create Module'
            }
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default AddModuleModal;
